import { useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from './Input';
import { Button } from './Button';

interface MapPickerProps {
  latitude?: number | null;
  longitude?: number | null;
  address?: string | null;
  onSelect: (data: { latitude: number; longitude: number; address: string }) => void;
}

export function MapPicker({ latitude, longitude, address, onSelect }: MapPickerProps) {
  const [addressInput, setAddressInput] = useState(address || '');
  const [lat, setLat] = useState(latitude?.toString() || '');
  const [lng, setLng] = useState(longitude?.toString() || '');

  const handleGeolocate = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const newLat = pos.coords.latitude;
        const newLng = pos.coords.longitude;
        setLat(newLat.toString());
        setLng(newLng.toString());
        onSelect({ latitude: newLat, longitude: newLng, address: addressInput });
      },
      () => { /* geolocation error — silent */ }
    );
  }, [addressInput, onSelect]);

  const handleConfirm = useCallback(() => {
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);
    if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
      onSelect({ latitude: parsedLat, longitude: parsedLng, address: addressInput });
    }
  }, [lat, lng, addressInput, onSelect]);

  return (
    <div className="space-y-3">
      <Input
        label="Dirección"
        placeholder="Ingresa la dirección de estacionamiento"
        value={addressInput}
        onChange={(e) => setAddressInput(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Latitud"
          placeholder="19.4326"
          value={lat}
          onChange={(e) => setLat(e.target.value)}
          type="number"
          step="any"
        />
        <Input
          label="Longitud"
          placeholder="-99.1332"
          value={lng}
          onChange={(e) => setLng(e.target.value)}
          type="number"
          step="any"
        />
      </div>

      {/* Map placeholder — static map preview */}
      <div className="relative w-full h-48 bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
        {lat && lng ? (
          import.meta.env.VITE_GOOGLE_MAPS_KEY ? (
            <img
              src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=400x200&markers=color:0xFDD238|${lat},${lng}&key=${import.meta.env.VITE_GOOGLE_MAPS_KEY || ''}`}
              alt="Map preview"
              className="w-full h-full object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-gray-500">
              {lat}, {lng}
            </div>
          )
        ) : null}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <MapPin size={32} className="text-brand-yellow drop-shadow-lg" />
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleGeolocate} className="flex-1">
          <MapPin size={16} className="mr-1" />
          Usar mi ubicación
        </Button>
        <Button variant="primary" size="sm" onClick={handleConfirm} className="flex-1">
          Confirmar ubicación
        </Button>
      </div>
    </div>
  );
}
